import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  ComposedChart, Area, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Flame, TrendingDown, TrendingUp, Minus, MousePointerClick } from "lucide-react";
import BodyMap, { AREA_LABELS } from "@/components/BodyMap";
import BodyRegionHoverChart from "@/components/BodyRegionHoverChart";
import ClinicBodyPartImpactPopup from "@/components/ClinicBodyPartImpactPopup";
import QuickStretchPlanDialog from "@/components/QuickStretchPlanDialog";
import {
  CHART_TOOLTIP_STYLE, CHART_GRID_STROKE, AXIS_COLOR, PAIN_DOMAIN, PIE_COLORS,
} from "@/lib/uiConstants";

const BAR_RADIUS = [6, 6, 0, 0];

const AREA_PALETTE = [
  "#dc2626", "#0f7d56", "#7c3aed", "#ea580c", "#0891b2",
  "#be185d", "#65a30d", "#854d0e", "#475569", "#9333ea",
  "#0e7490", "#b91c1c", "#15803d", "#a16207", "#1d4ed8",
];

function prettyArea(a) { return (a || "").replace(/_/g, " "); }

export default function ManagerStatistics() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [planTarget, setPlanTarget] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get("/clinic/analytics");
      setData(r.data);
    })();
  }, [reloadKey]);

  const trend = data?.trend || [];
  const hotspots = useMemo(() => data?.hotspots || [], [data]);
  const topHotspots = useMemo(() => data?.top_hotspots_30d || [], [data]);
  const byBodyPart = data?.by_body_part || {};
  const allAreas = useMemo(() => hotspots.map((h) => h.area), [hotspots]);
  const visibleAreas = useMemo(
    () => allAreas.filter((a) => !hidden.includes(a)),
    [allAreas, hidden],
  );
  const visibleHotspots = useMemo(
    () => hotspots.filter((h) => !hidden.includes(h.area)),
    [hotspots, hidden],
  );
  const colorFor = useMemo(() => {
    const map = {};
    allAreas.forEach((a, i) => { map[a] = AREA_PALETTE[i % AREA_PALETTE.length]; });
    return map;
  }, [allAreas]);

  const heatmap = useMemo(() => {
    const m = {};
    hotspots.forEach((h) => { m[h.area] = { avg: h.avg, reports: h.reports }; });
    return m;
  }, [hotspots]);

  const selectedInfo = selectedArea ? heatmap[selectedArea] : null;
  const selectedBase = useMemo(() => {
    if (!selectedArea) return null;
    return selectedArea.replace(/_(left|right)$/, "");
  }, [selectedArea]);

  const toggle = (area) =>
    setHidden((h) => (h.includes(area) ? h.filter((x) => x !== area) : [...h, area]));

  const openQuickPlan = (baseArea, exactArea) => {
    setPlanTarget({ area: exactArea || selectedArea || null, baseArea });
  };

  const legendItems = [
    { key: "deep",   label: "≥ 7.5 severe",     color: "#fbbf24" },
    { key: "orange", label: "6 – 7.5 high",     color: "#fcd34d" },
    { key: "amber",  label: "4.5 – 6 elevated", color: "#fde68a" },
    { key: "light",  label: "2.5 – 4.5 mild",   color: "#fef3c7" },
    { key: "pale",   label: "< 2.5 low",        color: "#fef9e6" },
    { key: "none",   label: "no data",          color: "#f4f4f5" },
  ];

  return (
    <div>
      <span className="pill">Clinic Statistics</span>
      <h1 className="mt-2 text-3xl font-bold">Aggregated clinic insights.</h1>
      <p className="text-zinc-500 mt-1">
        Anonymized across your clinic only. Tap a region on the model to see its stretching impact — brighter amber means higher clinic-wide pain.
      </p>

      {/* Top hotspots — clinic-wide, anonymized, last 30 days */}
      <div className="mt-8" data-testid="clinic-hotspots-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Flame className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
            Clinic-wide hotspots · last 30 days
          </h2>
          {topHotspots.length > 0 && (
            <span className="text-xs text-zinc-500">Δ shown vs previous 30 days · fully anonymized</span>
          )}
        </div>
        {topHotspots.length === 0 ? (
          <div className="card-elev text-sm text-zinc-500" data-testid="clinic-hotspots-empty">
            No painful regions logged across the clinic in the last 30 days.
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-4">
            {topHotspots.map((h, i) => {
              const trendKey = !h.has_prior ? "new" : h.delta > 0.3 ? "up" : h.delta < -0.3 ? "down" : "flat";
              const trendStyle = {
                up:   { Icon: TrendingUp,   color: "#dc2626", text: `+${h.delta} vs prev` },
                down: { Icon: TrendingDown, color: "#16a34a", text: `${h.delta} vs prev` },
                flat: { Icon: Minus,        color: "#71717a", text: "no change" },
                new:  { Icon: Flame,        color: "#d4af37", text: "new this period" },
              }[trendKey];
              const { Icon } = trendStyle;
              return (
                <div key={h.area} className="card-elev relative" data-testid={`clinic-hotspot-${i + 1}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-zinc-400">#{i + 1} HOTSPOT</div>
                      <div className="mt-1 text-lg font-bold capitalize">{prettyArea(h.area)}</div>
                    </div>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
                      style={{ background: "#fafafa", color: trendStyle.color, border: `1px solid ${trendStyle.color}22` }}
                      data-testid={`clinic-hotspot-${i + 1}-trend`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {trendStyle.text}
                    </span>
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-3xl font-black" style={{ color: "var(--gold-hover)" }}>{h.avg}</span>
                    <span className="text-xs text-zinc-500">avg pain · {h.reports} {h.reports === 1 ? "log" : "logs"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* HERO: the clinic pain model. The body silhouette is the main visual
          of the page; the popup for a focused region floats directly above
          it, echoing that region's own amber intensity. */}
      <section className="mt-12" data-testid="clinic-body-map-section">
        <div className="text-center mb-4">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500">Clinic pain map · last 30 days</div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-zinc-500">
            <MousePointerClick className="w-3.5 h-3.5" />
            Tap any spot to open its stretching-impact chart
          </div>
        </div>

        <ClinicBodyPartImpactPopup
          open={!!selectedArea}
          area={selectedArea}
          baseArea={selectedBase}
          avg={selectedInfo?.avg ?? null}
          reports={selectedInfo?.reports ?? 0}
          byBodyPart={byBodyPart}
          onClose={() => setSelectedArea(null)}
          onQuickPlan={openQuickPlan}
        />

        <div className="mx-auto" style={{ maxWidth: 640 }}>
          <BodyMap
            heatmap={heatmap}
            onRegionClick={(a) => setSelectedArea(a)}
            activeArea={selectedArea}
            large
            title="Clinic pain model"
            subtitle="Hover for a quick preview · tap to open full impact"
            renderHoverContent={(area) => (
              <BodyRegionHoverChart
                area={area}
                byBodyPart={byBodyPart}
                hasData={!!byBodyPart[area?.replace(/_(left|right)$/, "")]}
              />
            )}
          />
        </div>

        <div className="card-elev mt-4 mx-auto max-w-3xl" data-testid="clinic-body-map-legend">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-500">Amber-intensity legend</div>
            <div className="text-[11px] text-zinc-400">
              Deeper amber = higher clinic-wide avg pain
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            {legendItems.map((l) => (
              <div key={l.key} className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border" style={{ background: l.color, borderColor: "#00000022" }} />
                <span className="text-zinc-600">{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter chips — drive the smaller charts below */}
      {allAreas.length > 0 && (
        <div className="card-elev mt-10" data-testid="area-filter">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold">Filter areas · trend charts below</h3>
            <div className="flex gap-2">
              <button onClick={() => setHidden([])} className="btn-ghost text-xs h-8 px-3" data-testid="area-filter-all">Show all</button>
              <button onClick={() => setHidden([...allAreas])} className="btn-ghost text-xs h-8 px-3" data-testid="area-filter-none">Hide all</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {allAreas.map((a) => {
              const isOn = !hidden.includes(a);
              return (
                <button
                  key={a}
                  onClick={() => toggle(a)}
                  data-testid={`area-chip-${a}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition flex items-center gap-1.5 ${isOn ? "text-white border-transparent" : "bg-white border-zinc-200 text-zinc-400 line-through"}`}
                  style={isOn ? { background: colorFor[a] } : {}}
                >
                  <span className={`w-2 h-2 rounded-full ${isOn ? "bg-white/80" : ""}`} style={isOn ? {} : { background: colorFor[a] }} />
                  {prettyArea(a)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Secondary trend charts — smaller & tucked at the bottom */}
      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="card-elev lg:col-span-3">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <h3 className="font-bold text-base">Pain &amp; stretching trend</h3>
              <p className="text-[11px] text-zinc-500">Daily average pain per body part (shaded) vs. stretching sessions logged.</p>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "#2563eb" }} />Stretches</span>
            </div>
          </div>
          {visibleAreas.length === 0 && allAreas.length > 0 && (
            <div className="text-xs text-zinc-500 py-6 text-center">All areas hidden — use the chips above to show data.</div>
          )}
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={trend}>
              <defs>
                {visibleAreas.map((a) => (
                  <linearGradient key={a} id={`fill-${a}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colorFor[a]} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={colorFor[a]} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="date" stroke={AXIS_COLOR} fontSize={10} />
              <YAxis yAxisId="pain" domain={PAIN_DOMAIN} stroke={AXIS_COLOR} fontSize={10} />
              <YAxis yAxisId="stretches" orientation="right" stroke="#2563eb" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {visibleAreas.map((a) => (
                <Area
                  key={a} yAxisId="pain" type="monotone" dataKey={a} name={prettyArea(a)}
                  stroke={colorFor[a]} strokeWidth={2} fill={`url(#fill-${a})`} connectNulls dot={false} activeDot={{ r: 3 }}
                />
              ))}
              <Line yAxisId="stretches" type="monotone" dataKey="stretches" name="Stretches"
                stroke="#2563eb" strokeWidth={2.5} dot={{ r: 2, fill: "#2563eb" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="card-elev">
          <h3 className="font-bold text-sm mb-2">Injury hotspots</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={visibleHotspots} dataKey="reports" nameKey="area" outerRadius={65} innerRadius={35} paddingAngle={3}>
                {visibleHotspots.map((entry, i) => <Cell key={entry.area} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="card-elev lg:col-span-2">
          <h3 className="font-bold text-sm mb-2">Most-reported areas</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={visibleHotspots}>
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="area" stroke={AXIS_COLOR} fontSize={10} />
              <YAxis stroke={AXIS_COLOR} fontSize={10} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="reports" fill="#d4af37" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <QuickStretchPlanDialog
        open={!!planTarget}
        area={planTarget?.area || null}
        baseArea={planTarget?.baseArea || null}
        onClose={() => {
          setPlanTarget(null);
          setReloadKey((k) => k + 1);
        }}
        onCreated={() => {
          setReloadKey((k) => k + 1);
        }}
      />
    </div>
  );
}
